/**
 * Eyes, as pixel patterns stamped onto the face.
 *
 * An eye is a few pixels. Rendered from geometry it comes out a different few pixels every frame,
 * so the eyes are stamped on (raster.ts PixelStamp) at the point of the face where the skeleton puts
 * them, and the head's actual turn -- not the facing name -- picks the pattern: both eyes from the
 * front, the near one in profile, none from behind.
 *
 * Placement is calibrated to the LPC body (eye rows 29-31, columns 26-29 / 34-37 from the front);
 * the LOOK is a style. `lpc` is LPC's own eye, read off body/male/human/light.png; `ember`, the
 * default, is Ember's, so an Ember face is not mistaken for an LPC one.
 *
 * Pattern characters: n brow, # lash, c cool glint, a ember glint, w white, i iris, p pupil,
 * e the dark of an Ember eye; "." leaves the skin alone.
 */
import { EYE, Material } from "./palette"
import { DEFAULT_RASTER, FACING_YAW, Facing, PixelStamp, RasterOptions, cameraAxes, projectPoint } from "./raster"
import { Proportions, Skeleton } from "./rig"
import { Vec3, add, apply, dot, mul, rotY } from "./vec"

export interface EyeStyle {
    /** The screen-left and screen-right eye seen from the front, and the near eye looking left. */
    front: [string[], string[]]
    profile: string[]
    /** Where the eye's centre falls inside the pattern, in pixels from its top-left. */
    center: { x: number; y: number }
    /** Iris and pupil, unless the look names its own. */
    iris: number
    pupil: number
}

export const EYE_STYLES: Record<string, EyeStyle> = {
    lpc: {
        front: [
            [".nn.", "c##c", "w#iw", ".ppw"],
            [".nn.", "c##c", "wi#w", "wpp."],
        ],
        profile: [".nn.", "##cc", "i#ww", "ppw."],
        center: { x: 2, y: 2.5 },
        iris: EYE.iris,
        pupil: EYE.pupil,
    },
    // Ember's own: a tall near-black eye with the fire's amber catch-light and a short brow
    // slanted outward. No white and no cool glint, which is what makes an LPC eye read as LPC.
    ember: {
        front: [
            ["nn..", ".ae.", ".ee.", ".ee."],
            ["..nn", ".ae.", ".ee.", ".ee."],
        ],
        profile: ["nn..", "ae..", "ee..", "ee.."],
        center: { x: 2, y: 2 },
        iris: EYE.warmPupil,
        pupil: EYE.warmPupil,
    },
    // A head drawn at real proportions is a few pixels across: each eye is one dark pixel.
    dot: {
        front: [["e"], ["e"]],
        profile: ["e"],
        center: { x: 0.5, y: 0.5 },
        iris: EYE.warmPupil,
        pupil: EYE.warmPupil,
    },
    // The realist's dot eye with a lash at its outer corner: a woman's face at this size.
    lash: {
        front: [
            ["#..", ".e."],
            ["..#", ".e."],
        ],
        profile: ["#..", ".e."],
        center: { x: 1.5, y: 1.5 },
        iris: EYE.warmPupil,
        pupil: EYE.warmPupil,
    },
    // A dead eye: sunk dark in its socket, a milky glint where a living one has its pupil.
    zombie: {
        front: [["ew"], ["we"]],
        profile: ["we"],
        center: { x: 1, y: 0.5 },
        iris: EYE.warmPupil,
        pupil: EYE.warmPupil,
    },
    // A wolfman's: LPC's pale cyan glow, a pixel each, set in the dark of the fur.
    wolf: {
        front: [["g"], ["g"]],
        profile: ["g"],
        center: { x: 0.5, y: 0.5 },
        iris: EYE.wolf,
        pupil: EYE.wolf,
    },
}

export const DEFAULT_EYE_STYLE = "ember"

const mirror = (rows: string[]) => rows.map(row => row.split("").reverse().join(""))

/** Above this share of the head's forward turned toward the camera, both eyes show. */
const FRONT_TURN = 0.5
/** Above this share turned sideways, the near eye shows in profile. */
const PROFILE_TURN = 0.6

export const faceStamps = (
    sk: Skeleton,
    p: Proportions,
    skin: Material,
    facing: Facing,
    styleName: string = DEFAULT_EYE_STYLE,
    o: RasterOptions = DEFAULT_RASTER,
): PixelStamp[] => {
    const style = EYE_STYLES[styleName] ?? EYE_STYLES[DEFAULT_EYE_STYLE]
    const yaw = rotY(FACING_YAW[facing])
    const { right, back } = cameraAxes(o.pitch)
    const head = mul(yaw, sk.headBasis)
    const forward = apply(head, [0, 0, 1])
    const toward = dot(forward, back)
    const sideways = dot(forward, right)
    const palette = {
        n: skin.line,
        "#": skin.outline,
        c: EYE.glint,
        a: EYE.ember,
        w: EYE.white,
        i: style.iris,
        p: style.pupil,
        e: style.pupil,
        g: EYE.wolf,
    }
    // A point on the head, in head-local coordinates, to frame pixels.
    const at = (local: Vec3) => projectPoint(apply(yaw, add(sk.head, apply(sk.headBasis, local))), o)
    const stamp = (local: Vec3, rows: string[]): PixelStamp => {
        const q = at(local)
        return { x: Math.round(q.x - style.center.x), y: Math.round(q.y - style.center.y), rows, palette, group: "head" }
    }
    const [a, , c] = p.head
    const front = c * 0.97

    if (toward > FRONT_TURN) {
        const eyes = [-1, 1].map(s => {
            const local: Vec3 = [s * p.eyeSpacing, p.eyeHeight, front]
            return { local, x: at(local).x }
        })
        eyes.sort((l, r) => l.x - r.x)
        return [stamp(eyes[0].local, style.front[0]), stamp(eyes[1].local, style.front[1])]
    }
    if (toward > 0 && Math.abs(sideways) > PROFILE_TURN) {
        // The near eye is on whichever side of the head faces the camera.
        const near = dot(apply(head, [1, 0, 0]), back) > 0 ? 1 : -1
        // The side facings stand three-quarters on (raster.ts SIDE_TURN), so the near eye is on the
        // face, toward its near cheek; profileEyeInset sets it back from the face's front edge.
        const local: Vec3 = [near * a * 0.8, p.eyeHeight, front - p.profileEyeInset]
        const side = stamp(local, sideways < 0 ? style.profile : mirror(style.profile))
        // On the rows the eyes would be on from the front, in this same pose: that spot on a turned
        // face is nearer the camera, and the tilt would otherwise drop a side eye a row below the
        // front ones. LPC keeps them level across facings.
        const level = projectPoint(add(sk.head, [0, p.eyeHeight, front]), o)
        return [{ ...side, y: Math.round(level.y - style.center.y) }]
    }
    return []
}
